//
// Copyright (c) 2025 Comcast
//

import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { Link } from 'react-router'

import SidebarSection from 'components/Shared/Sidebar/SidebarSection/Index'
import Table from 'components/Shared/TableNew/Index'
import TableBody from 'components/Shared/TableNew/TableBody'
import TableRow from 'components/Shared/TableNew/TableRow'
import TableCell from 'components/Shared/TableNew/TableCell'
import TableHead from 'components/Shared/TableNew/TableHead'
import TableRowNoData from 'components/Shared/TableNew/TableRowNoData'
import Badge from 'components/Shared/Badge/Index'
import Tooltip from 'components/Shared/Tooltip/Index'
import EnabledIcon from 'components/Shared/EnabledIcon/Index'
import ScrollArea from 'components/Shared/ScrollArea/Index'

const StyledScrollArea = styled(ScrollArea)`
  max-height: 600px;
`

export const CellContent = styled.div`
  overflow-wrap: anywhere;
  ${props => props.$noWrap && css`
    white-space: nowrap;
  `}
`

const LinkWrapper = styled.div`
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  line-height: normal;
`

const StyledLink = styled(Link)`
  font-size: 14px;
  line-height: 16px;
  font-weight: 600;
`

const getCellContent = (item, col, columnKey) => {
  let value = item[columnKey]

  // optionally map values associated with form's Select controls
  if (col.valuesMap) {
    value = col.valuesMap[value]
  }
  const isBadge = Boolean(col.format === 'badge' || col.type === 'array')
  const isLink = Boolean(col.format === 'link' && col.linkKey && item[col.linkKey])
  const isSwitch = Boolean(col.type === 'boolean')

  let result = value

  if (isLink) {
    const linkTo = item[col.linkKey]
    const tooltipId = `sidebar-table-tooltip-${value}`
    result = (
      <LinkWrapper>
        <StyledLink
          to={linkTo}
          data-tooltip-content={value}
          data-tooltip-id={tooltipId}
        >
          {value}
        </StyledLink>
        <Tooltip id={tooltipId} />
      </LinkWrapper>
    )
  } else if (isBadge) {
    const badges = [value].flat().filter(Boolean)
    result = badges.map((badge, index) => (
      <Badge key={index} margin='2px' color={col.colorsMap?.[badge]}>{badge}</Badge>
    ))
  } else if (isSwitch) {
    result = <EnabledIcon enabled={value} />
  }

  return result
}

/* Example:
  values={[
    { id: '123', title: 'Ticket 123', role: 'user', link: 'httpd://example1.com', children: ['a', 'b'], isRoot: false },
    { id: '432', title: 'Ticket 432', role: 'admin', link: 'httpd://example2.com', children: ['c'], isRoot: true },
  ]}
  columnsOrder={['id', 'title', 'role']}
  columns={{
    'id': {
      title: 'Ticket Id',
    },
    'title': {
      title: 'Title',
      format: 'link',
      linkKey: 'link',
    },
    'role': {
      title: 'Title',
      format: 'badge',
      valuesMap: {
        'user': 'User',
        'admin': 'Admin',
      },
      colorsMap: {
        'User': colors.success73,
        'Admin': colors.alert85,
      },
    },
    'children': {
      'isRequired': false,
      'type': 'array',
      'title': 'Children array',
    },
    'isRoot': {
      'isRequired': true,
      'type': 'boolean',
      'title': 'Is Root',
    },
  }}
*/
const SidebarSectionTable = ({ title, values, columnsOrder, columns }) => {
  return (
    <SidebarSection
      title={title}
      data-test='sidebar-section-table'
    >
      <StyledScrollArea>
        <Table width='100%'>
          <TableHead>
            <TableRow>
              {columnsOrder.map((columnKey) => {
                const title = columns[columnKey].title
                return (
                  <TableCell key={columnKey}>{title}</TableCell>
                )
              })}
            </TableRow>
          </TableHead>
          <TableBody hoverable={false}>
            {values.map((item, index) => {
              return (
                <TableRow key={index}>
                  {columnsOrder.map((columnKey) => {
                    const col = columns[columnKey]
                    return (
                      <TableCell key={columnKey}>
                        <CellContent $noWrap={col.noWrap}>
                          {getCellContent(item, col, columnKey)}
                        </CellContent>
                      </TableCell>
                    )
                  })}
                </TableRow>
              )
            })}
            <TableRowNoData data={values} />
          </TableBody>
        </Table>
      </StyledScrollArea>
    </SidebarSection>
  )
}

SidebarSectionTable.propTypes = {
  title: PropTypes.string,
  values: PropTypes.arrayOf(PropTypes.object),
  columnsOrder: PropTypes.arrayOf(PropTypes.string),
  columns: PropTypes.objectOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      format: PropTypes.string,
      linkKey: PropTypes.string,
      valuesMap: PropTypes.object,
      colorsMap: PropTypes.object,
      type: PropTypes.string,
      noWrap: PropTypes.bool,
    })
  ),
}

export default SidebarSectionTable
